import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import {
  FiMoreHorizontal,
  FiTrash2,
  FiEye,
  FiTrendingUp,
  FiChevronDown,
  FiChevronRight,
  FiSearch,
  FiBookOpen,
  FiClock,
  FiBarChart2,
  FiRefreshCw,
  FiSliders,
  FiEdit2,
  FiShield,
} from "react-icons/fi";
import {
  Header,
  CustomPagination,
  TableDropdown,
  SkeletonTable,
  ConfirmationModal,
  AddTestModal,
  StudentLoginAccessModal,
} from "../components";
import EditTestModal from "../components/teacherModule/onlineTest/EditTestModal";
import PublishTestErrorModal, {
  parsePublishPointsError,
  getPublishErrorMessage,
} from "../components/PublishTestErrorModal";
import { useStateContext } from "../contexts/ContextProvider";
import { toast } from "react-toastify";
//import useKYCStatusRedirect from '../hooks/useKYCStatusRedirect';
import * as Icons from "../data/class";
import { generateSchoolSessions, displaySchoolTerm } from "../utils/schoolYearUtils";

const CBTExams = ({ history }) => {
  //useKYCStatusRedirect("/cbt-exams");
  const { school, user, refreshSchool } = useStateContext();
  const location = useLocation();
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;
  const scrollContainerRef = useRef(null);

  const [tests, setTests] = useState([]);
  const [groupedTests, setGroupedTests] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [expandedClasses, setExpandedClasses] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // { details, test } when publishing is blocked because question marks don't add up
  const [publishPointsError, setPublishPointsError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [filterStatus, setFilterStatus] = useState("all"); // 'all', 'draft', 'published', 'completed'
  const [sortBy, setSortBy] = useState("created"); // 'created', 'title', 'status'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [editingTest, setEditingTest] = useState(null);
  const [autoSubmitTest, setAutoSubmitTest] = useState(school?.settings?.auto_submit_test ?? false);
  const [autoSubmitSaving, setAutoSubmitSaving] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  // Term & Session filter state (persisted separately)
  const [termFilter, setTermFilter] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("cbtExamsSelectedTerm") || "null");
      return saved?.id || schoolTerm || "1";
    } catch (error) {
      console.error("Error loading termFilter from storage:", error);
    }
    return schoolTerm || "1";
  });
  const [sessionFilter, setSessionFilter] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("cbtExamsSelectedSession") || "null");
      return saved?.id || schoolSession || "2024/2025";
    } catch (error) {
      console.error("Error loading sessionFilter from storage:", error);
    }
    return schoolSession || "2024/2025";
  });

  // Generate school sessions for session filter
  const schoolSessions = generateSchoolSessions(2020, 10);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    try {
      const savedState = localStorage.getItem("cbtExamsState");
      if (savedState) {
        const state = JSON.parse(savedState);
        return state.perPage || 20;
      }
    } catch (error) {
      console.error("Error loading perPage from storage:", error);
    }
    return 20;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);

  // State persistence functions
  const saveStateToStorage = () => {
    const state = {
      expandedClasses,
      viewMode,
      filterStatus,
      sortBy,
      searchText,
      perPage,
      scrollPosition: scrollContainerRef.current?.scrollTop || 0,
    };
    localStorage.setItem("cbtExamsState", JSON.stringify(state));
  };

  const loadStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem("cbtExamsState");
      if (savedState) {
        const state = JSON.parse(savedState);
        setExpandedClasses(state.expandedClasses || {});
        setViewMode(state.viewMode || "grid");
        setFilterStatus(state.filterStatus || "all");
        setSortBy(state.sortBy || "created");
        setSearchText(state.searchText || "");
        // perPage is initialized in useState from localStorage

        // Restore scroll position after a short delay
        setTimeout(() => {
          if (scrollContainerRef.current && state.scrollPosition) {
            scrollContainerRef.current.scrollTop = state.scrollPosition;
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading saved state:", error);
    }
  };

  // Check if we're returning from a test detail page
  const checkReturnFromDetail = () => {
    const state = location.state;
    if (state?.returnFromDetail && state?.lastViewedClass) {
      // Auto-expand the class that was being viewed
      setExpandedClasses((prev) => ({
        ...prev,
        [state.lastViewedClass]: true,
      }));

      // Clear the state to prevent re-expansion on refresh
      history.replace(location.pathname, {});
    }
  };

  const fetchTests = async (
    page = currentPage,
    limit = perPage,
    search = searchText,
    term = termFilter,
    session = sessionFilter,
  ) => {
    if (!schoolId) return;

    try {
      setLoading(true);
      // Build query string with search, term and session parameters
      let queryString = `test?school_id=${schoolId}&page=${page}&limit=${limit}`;
      if (search && search.trim()) {
        queryString += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (term && term !== "all") {
        queryString += `&term=${term}`;
      }
      if (session && session !== "all") {
        queryString += `&session=${encodeURIComponent(session)}`;
      }

      const { data } = await userRequest.get(queryString);
      //console.log('Tests data:', data);
      const fetchedTests = data.tests || [];
      setTests(fetchedTests);

      // Store pagination metadata
      setTotalPages(data.total_pages || 1);
      setTotalDocuments(data.total_documents || 0);
      setCurrentPage(data.page || 1);

      // Group tests by class / class group
      const grouped = {};
      fetchedTests.forEach((test) => {
        const classId = test.class?._id || test.class_group_id || "unassigned";
        const className =
          test.class?.class_name ||
          (test.class_group_id ? "Shared Class Group" : "Admission / General");

        if (!grouped[classId]) {
          grouped[classId] = {
            classId,
            className,
            tests: [],
          };
        }

        grouped[classId].tests.push(test);
      });

      setGroupedTests(grouped);
    } catch (error) {
      console.error("Error fetching tests:", error);
      setError("Failed to fetch tests");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    if (school?.code) {
      try {
        const { data } = await userRequest.get(`classes?code=${school.code}`);
        setClasses(data.classes || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
        setClasses([]);
      }
    }
  };

  const fetchAllSubjects = async () => {
    if (!schoolId || classes.length === 0) {
      setSubjects([]);
      return;
    }

    try {
      const allSubjects = [];

      // Fetch subjects for each class
      const subjectPromises = classes.map(async (classItem) => {
        try {
          const { data } = await userRequest.get(
            `/subjects/${schoolId}/${classItem._id}?limit=100`,
          );
          if (data.subjects && Array.isArray(data.subjects)) {
            // Add class information to each subject
            return data.subjects.map((subject) => ({
              ...subject,
              class: classItem,
            }));
          }
          return [];
        } catch (error) {
          console.error(`Error fetching subjects for class ${classItem._id}:`, error);
          return [];
        }
      });

      const subjectArrays = await Promise.all(subjectPromises);
      // Flatten the array of arrays into a single array
      const flattenedSubjects = subjectArrays.flat();
      setSubjects(flattenedSubjects);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    }
  };

  const toggleCreateModal = () => {
    setIsCreateModalOpen(!isCreateModalOpen);
  };

  useEffect(() => {
    loadStateFromStorage();
    checkReturnFromDetail();
    fetchTests(1, perPage, searchText); // Always start from page 1 on initial load
    fetchClasses();
  }, [schoolId, school?.code]);

  // Handle search with debouncing
  useEffect(() => {
    if (!schoolId) return;

    const searchTimer = setTimeout(() => {
      // Reset to page 1 when search changes
      setCurrentPage(1);
      fetchTests(1, perPage, searchText);
    }, 500); // 500ms debounce

    return () => clearTimeout(searchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    if (classes.length > 0) {
      fetchAllSubjects();
    }
  }, [classes, schoolId]);

  // Save state whenever it changes
  useEffect(() => {
    saveStateToStorage();
  }, [expandedClasses, viewMode, filterStatus, sortBy, searchText, perPage]);

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        const state = JSON.parse(localStorage.getItem("cbtExamsState") || "{}");
        state.scrollPosition = scrollTop;
        localStorage.setItem("cbtExamsState", JSON.stringify(state));
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const toggleClassExpansion = (classId) => {
    setExpandedClasses((prev) => ({
      ...prev,
      [classId]: !prev[classId],
    }));
  };

  const expandAllClasses = () => {
    const allExpanded = {};
    Object.keys(groupedTests).forEach((classId) => {
      allExpanded[classId] = true;
    });
    setExpandedClasses(allExpanded);
  };

  const collapseAllClasses = () => {
    setExpandedClasses({});
  };

  const navigateToTestDetail = (test, action) => {
    // Save the current class context before navigating
    const classId = test.class?._id || test.class_group_id || "unassigned";
    const currentState = {
      returnFromDetail: true,
      lastViewedClass: classId,
      action: action,
    };

    if (action === "view") {
      history.push({
        pathname: `/cbt-exams/view/${test._id}`,
        state: currentState,
      });
    } else if (action === "results") {
      history.push({
        pathname: `/cbt-exams/results/${test._id}/${schoolId}`,
        state: {
          ...currentState,
          testDetails: {
            _id: test._id,
            title: test.title,
            description: test.description,
            subject: test.subject,
            class: test.class,
            type: test.type,
            ca: test.ca,
            total_score: test.total_score,
            duration: test.duration,
            status: test.status,
            is_active: test.is_active,
            createdAt: test.createdAt,
          },
        },
      });
    }
  };

  const handleStatusToggle = (test) => {
    setDropdownIndex(null);
    setSelectedTest(test);
    setConfirmAction("status");
    setShowConfirmModal(true);
  };

  const handleEditTest = (test) => {
    setDropdownIndex(null);
    setEditingTest(test);
  };

  const handleDeleteTest = (test) => {
    setDropdownIndex(null);
    setSelectedTest(test);
    setConfirmAction("delete");
    setShowConfirmModal(true);
  };

  const confirmActionHandler = async () => {
    if (!selectedTest) return;

    if (confirmAction === "status") {
      setActionLoading(true);
      try {
        const newStatus = selectedTest.status === "draft" ? "published" : "draft";
        // A shared test has no single class — test.class here is the class GROUP shown for
        // display purposes (enriched server-side), not a real class id, and must never be sent
        // back as `class` on an edit (that would silently overwrite class_group_id scoping).
        const isSharedTest = Boolean(selectedTest.class_group_id);
        const requestBody = {
          title: selectedTest.title,
          description: selectedTest.description,
          duration: selectedTest.duration,
          total_score: selectedTest.total_score,
          school: selectedTest.school._id,
          ...(isSharedTest
            ? {}
            : { class: selectedTest.class._id, subject: selectedTest.subject?._id }),
          is_active: selectedTest.is_active,
          term: selectedTest.term,
          session: selectedTest.session,
          type: selectedTest.type,
          ca: selectedTest.ca,
          status: newStatus,
        };

        await userRequest.put(`/test/${selectedTest._id}`, requestBody);

        // Refresh the current page to get updated data
        fetchTests(currentPage, perPage, searchText);

        toast.success(
          `Test ${newStatus === "published" ? "published" : "moved to draft"} successfully!`,
        );
      } catch (error) {
        console.error("Error updating test status:", error);
        const pointsError = parsePublishPointsError(error);
        if (pointsError) {
          setPublishPointsError({ details: pointsError, test: selectedTest });
        } else {
          toast.error(getPublishErrorMessage(error));
        }
      } finally {
        setActionLoading(false);
      }
    } else if (confirmAction === "delete") {
      setDeleting(true);
      try {
        await userRequest.delete(`/test/${selectedTest._id}`);

        // Refresh the current page to get updated data
        // If current page becomes empty, go to previous page
        const shouldGoToPrevPage = tests.length === 1 && currentPage > 1;
        if (shouldGoToPrevPage) {
          setCurrentPage(currentPage - 1);
          fetchTests(currentPage - 1, perPage, searchText);
        } else {
          fetchTests(currentPage, perPage, searchText);
        }

        toast.success("Test deleted successfully!");
      } catch (error) {
        console.error("Error deleting test:", error);
        toast.error("Failed to delete test");
      } finally {
        setDeleting(false);
      }
    }

    // Close modal and reset state
    setShowConfirmModal(false);
    setSelectedTest(null);
    setConfirmAction(null);
  };

  const cancelActionHandler = () => {
    setShowConfirmModal(false);
    setSelectedTest(null);
    setConfirmAction(null);
  };

  useEffect(() => {
    setAutoSubmitTest(school?.settings?.auto_submit_test ?? false);
  }, [school?.settings?.auto_submit_test]);

  const handleAutoSubmitToggle = async () => {
    const newValue = !autoSubmitTest;
    setAutoSubmitTest(newValue);
    setAutoSubmitSaving(true);
    try {
      await userRequest.post(`/schools/${schoolId}/settings`, {
        auto_submit_test: newValue,
      });
      toast.success(`Auto-submit ${newValue ? "enabled" : "disabled"}`);
      refreshSchool();
    } catch (error) {
      setAutoSubmitTest(!newValue);
      toast.error("Failed to update setting");
    } finally {
      setAutoSubmitSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: "bg-slate-100", text: "text-slate-700", label: "Draft" },
      published: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Published" },
      completed: { bg: "bg-blue-100", text: "text-blue-700", label: "Completed" },
      cancelled: { bg: "bg-rose-100", text: "text-rose-700", label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.bg} ${config.text} border border-opacity-20`}
      >
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type, ca) => {
    if (type === "ca") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-violet-100 text-violet-700 border border-violet-200">
          CA {ca}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
        {type?.toUpperCase() || "EXAM"}
      </span>
    );
  };

  // Filter and sort functions
  const getFilteredAndSortedTests = () => {
    let filtered = Object.values(groupedTests);

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered
        .map((classGroup) => ({
          ...classGroup,
          tests: classGroup.tests.filter((test) => (test?.status || "") === filterStatus),
        }))
        .filter((classGroup) => classGroup.tests.length > 0);
    }

    // Search is now handled server-side, so no client-side filtering needed

    // Apply sorting
    filtered.forEach((classGroup) => {
      classGroup.tests.sort((a, b) => {
        switch (sortBy) {
          case "title":
            const titleA = a.title || "";
            const titleB = b.title || "";
            return titleA.localeCompare(titleB);
          case "status":
            const statusA = a.status || "";
            const statusB = b.status || "";
            return statusA.localeCompare(statusB);
          case "created":
          default:
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateB - dateA;
        }
      });
    });

    return filtered;
  };

  const getClassStats = (classGroup) => {
    const tests = classGroup?.tests || [];
    const total = tests.length;
    const published = tests.filter((t) => t?.status === "published").length;
    const draft = tests.filter((t) => t?.status === "draft").length;
    const completed = tests.filter((t) => t?.status === "completed").length;

    return { total, published, draft, completed };
  };

  // Calculate stats - use totalDocuments for total, but calculate active/published from current page
  const totalTests = totalDocuments; // Use total from API
  const activeTests = tests.filter((test) => test.is_active).length; // Current page only
  const publishedTests = tests.filter((test) => test.status === "published").length; // Current page only
  const totalClasses = Object.keys(groupedTests).length; // Current page only
  const filteredTests = getFilteredAndSortedTests();

  return (
    <>
      <div className="px-4 md:px-6 pt-4 md:pt-6 mt-20 md:mt-0">
        <div className="flex justify-between items-center">
          <Header category="Assessment" title="CBT Exams" />
          <div className="flex items-center space-x-3">
            <button
              onClick={() => fetchTests(currentPage, perPage, searchText)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FiRefreshCw className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Refresh</span>
            </button>
            {/*<button
              type="button"
              onClick={toggleCreateModal}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FiBookOpen className="w-4 h-4 mr-2" />
              Create Test
            </button>*/}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2 mt-3 md:mt-5 mx-2 md:mx-4 p-1 md:p-4">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-xs md:text-sm">Total Tests</p>
              <p className="text-xl md:text-2xl font-semibold">{totalTests}</p>
            </div>
            <button
              type="button"
              className="bg-[#3371E8] text-white opacity-90 hover:drop-shadow-xl rounded-xl p-3 md:p-4"
            >
              <img src={Icons.coupon} alt="Test Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-xs md:text-sm">Active Tests</p>
              <p className="text-xl md:text-2xl font-semibold">{activeTests}</p>
            </div>
            <button
              type="button"
              className="bg-[#41D3BD] text-white opacity-90 hover:drop-shadow-xl rounded-xl p-3 md:p-4"
            >
              <img src={Icons.taskDaily} alt="Active Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-xs md:text-sm">Published</p>
              <p className="text-xl md:text-2xl font-semibold">{publishedTests}</p>
            </div>
            <button
              type="button"
              className="bg-[#E26E6A] text-white opacity-90 hover:drop-shadow-xl rounded-xl p-3 md:p-4"
            >
              <img src={Icons.checkList} alt="Published Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg rounded-3xl p-4 md:p-8 md:pt-9">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-xs md:text-sm">Classes</p>
              <p className="text-xl md:text-2xl font-semibold">{totalClasses}</p>
            </div>
            <button
              type="button"
              className="bg-[#FB9678] text-white opacity-90 hover:drop-shadow-xl rounded-xl p-3 md:p-4"
            >
              <img src={Icons.userMultiple} alt="Class Icon" />
            </button>
          </div>
        </div>
      </div>

      {/* CBT Settings & Access Control */}
      <div className="px-3 md:px-6 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Auto-submit Setting */}
        <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-orange-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Auto-submit on tab switch</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Submits test if student leaves exam window
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span
              className={`text-xs font-medium ${autoSubmitTest ? "text-green-600" : "text-slate-400"}`}
            >
              {autoSubmitTest ? "Enabled" : "Disabled"}
            </span>
            {autoSubmitSaving && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent" />
            )}
            <button
              onClick={handleAutoSubmitToggle}
              disabled={autoSubmitSaving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 ${
                autoSubmitTest ? "bg-indigo-600" : "bg-slate-200"
              }`}
              role="switch"
              aria-checked={autoSubmitTest}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  autoSubmitTest ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Student Login Access Control */}
        <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FiShield className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-800">Student Login & CBT Access</p>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {school?.settings?.result?.parent_payment_threshold || 0}% Threshold
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                View blocked students and grant term login exemptions
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsAccessModalOpen(true)}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-xl shadow-sm hover:shadow transition-all flex items-center gap-1.5 flex-shrink-0"
          >
            <FiShield className="w-3.5 h-3.5" />
            Manage Access
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-3 md:px-6 pb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Enhanced Header with Controls */}
          <div className="px-6 py-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Test Management</h2>
                {/*<p className="text-slate-600">Organize and manage your CBT examinations by class</p>*/}
              </div>

              {/* Controls */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4">
                {/* Search */}
                <div className="relative w-full md:w-80">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search tests, subjects, or classes..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 w-full sm:w-80"
                  />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 items-center justify-start md:justify-end">
                  {/* Primary filters: Term & Session (always visible) */}
                  <select
                    value={termFilter}
                    onChange={(e) => {
                      const newTerm = e.target.value;
                      setTermFilter(newTerm);
                      localStorage.setItem(
                        "cbtExamsSelectedTerm",
                        JSON.stringify({
                          id: newTerm,
                          name: newTerm === "all" ? "All Terms" : `Term ${newTerm}`,
                        }),
                      );
                      setCurrentPage(1);
                      fetchTests(1, perPage, searchText, newTerm, sessionFilter);
                    }}
                    className="px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm min-w-[140px]"
                  >
                    <option value="all">All Terms</option>
                    <option value="1">{displaySchoolTerm(1) || "1st Term"}</option>
                    <option value="2">{displaySchoolTerm(2) || "2nd Term"}</option>
                    <option value="3">{displaySchoolTerm(3) || "3rd Term"}</option>
                  </select>

                  <select
                    value={sessionFilter}
                    onChange={(e) => {
                      const newSession = e.target.value;
                      setSessionFilter(newSession);
                      const sessionLabel =
                        newSession === "all"
                          ? "All Sessions"
                          : schoolSessions.find((s) => s.value === newSession)?.label || newSession;
                      localStorage.setItem(
                        "cbtExamsSelectedSession",
                        JSON.stringify({
                          id: newSession,
                          name: sessionLabel,
                        }),
                      );
                      setCurrentPage(1);
                      fetchTests(1, perPage, searchText, termFilter, newSession);
                    }}
                    className="px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm min-w-[150px]"
                  >
                    <option value="all">All Sessions</option>
                    {schoolSessions.map((session) => (
                      <option key={session.value} value={session.value}>
                        {session.label}
                      </option>
                    ))}
                  </select>

                  {/* Advanced filters: Status & Sort in dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAdvancedFilters((prev) => !prev)}
                      className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-200 rounded-xl bg-white text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <FiSliders className="w-4 h-4 text-slate-500" />
                      <span>More filters</span>
                    </button>

                    {showAdvancedFilters && (
                      <div className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-3 space-y-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-500">Status</span>
                          <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-2.5 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                          >
                            <option value="all">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="completed">Completed</option>
                          </select>
                        </div>

                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-slate-500">Sort by</span>
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-2.5 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                          >
                            <option value="created">Date (newest)</option>
                            <option value="title">Title (A–Z)</option>
                            <option value="status">Status</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-600">Quick Actions:</span>
                <button
                  onClick={expandAllClasses}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Expand All
                </button>
                <span className="text-slate-300">•</span>
                <button
                  onClick={collapseAllClasses}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Collapse All
                </button>
              </div>
              <div className="text-sm text-slate-500">
                {filteredTests.length} class{filteredTests.length !== 1 ? "es" : ""} • {totalTests}{" "}
                total tests
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
              Test updated successfully!
            </div>
          )}

          {/* Classes and Tests */}
          <div ref={scrollContainerRef} className="max-h-[calc(100vh-300px)] overflow-y-auto">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading tests...</p>
              </div>
            ) : filteredTests.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                  <FiBookOpen className="w-16 h-16" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No tests found</h3>
                <p className="text-slate-500 text-sm">
                  {searchText || filterStatus !== "all"
                    ? "Try adjusting your search or filter criteria"
                    : "Create your first test to get started with CBT examinations"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredTests.map((classGroup) => {
                  const stats = getClassStats(classGroup);
                  const isExpanded = expandedClasses[classGroup.classId];

                  return (
                    <div key={classGroup.classId} className="bg-white">
                      {/* Class Header */}
                      <div
                        className="px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => toggleClassExpansion(classGroup.classId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                isExpanded
                                  ? "bg-blue-100 text-blue-600"
                                  : "bg-slate-100 text-slate-400"
                              }`}
                            >
                              {isExpanded ? (
                                <FiChevronDown className="w-5 h-5" />
                              ) : (
                                <FiChevronRight className="w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-800">
                                {classGroup.className}
                              </h3>
                              <div className="flex items-center space-x-4 mt-1">
                                <div className="flex items-center space-x-2 text-sm text-slate-600">
                                  <FiBookOpen className="w-4 h-4" />
                                  <span>
                                    {stats.total} test{stats.total !== 1 ? "s" : ""}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-xs">
                                  {stats.published > 0 && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                                      {stats.published} Published
                                    </span>
                                  )}
                                  {stats.draft > 0 && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                                      {stats.draft} Draft
                                    </span>
                                  )}
                                  {stats.completed > 0 && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                      {stats.completed} Completed
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-slate-500">
                            Click to {isExpanded ? "collapse" : "expand"}
                          </div>
                        </div>
                      </div>

                      {/* Tests List */}
                      {isExpanded && (
                        <div className="bg-slate-50 border-t border-slate-200">
                          <div className="px-6 py-4">
                            <div className="grid gap-4">
                              {classGroup.tests.map((test) => (
                                <div
                                  key={test._id}
                                  className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-all duration-200"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-3 mb-2">
                                        <h4 className="text-base font-semibold text-slate-800 truncate">
                                          {test.title}
                                        </h4>
                                        {getStatusBadge(test.status)}
                                        {getTypeBadge(test.type, test.ca)}
                                      </div>

                                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                                        {test.description}
                                      </p>

                                      <div className="flex items-center space-x-6 text-sm text-slate-500">
                                        <div className="flex items-center space-x-1">
                                          <FiBookOpen className="w-4 h-4" />
                                          <span>{test.subject?.subject_name || "General"}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <FiBarChart2 className="w-4 h-4" />
                                          <span>{test.total_score} points</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <FiClock className="w-4 h-4" />
                                          <span>{test.duration} mins</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <span className="text-xs">
                                            {new Date(test.createdAt).toLocaleDateString()}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-2 ml-4">
                                      <button
                                        onClick={() => navigateToTestDetail(test, "view")}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                      >
                                        <FiEye className="w-4 h-4 mr-1" />
                                        View
                                      </button>
                                      <button
                                        onClick={() => navigateToTestDetail(test, "results")}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                                      >
                                        <FiTrendingUp className="w-4 h-4 mr-1" />
                                        Results
                                      </button>
                                      <div className="relative">
                                        <button
                                          onClick={(e) => handleDropdownClick(test._id, e)}
                                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                        >
                                          <FiMoreHorizontal className="w-4 h-4" />
                                        </button>
                                        <TableDropdown
                                          isVisible={dropdownIndex === test._id}
                                          position={dropdownPosition}
                                          options={[
                                            {
                                              label: "View Test Questions",
                                              icon: <FiEye className="w-4 h-4 text-blue-600" />,
                                              onClick: () => navigateToTestDetail(test, "view"),
                                            },
                                            {
                                              label: "Edit Settings",
                                              icon: <FiEdit2 className="w-4 h-4 text-amber-600" />,
                                              onClick: () => handleEditTest(test),
                                            },
                                            {
                                              label: "View Results",
                                              icon: (
                                                <FiTrendingUp className="w-4 h-4 text-purple-600" />
                                              ),
                                              onClick: () => navigateToTestDetail(test, "results"),
                                            },
                                            {
                                              label: "Delete Test",
                                              icon: <FiTrash2 className="w-4 h-4 text-red-600" />,
                                              onClick: () => handleDeleteTest(test),
                                            },
                                          ]}
                                          onClose={handleCloseDropdown}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && filteredTests.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Showing page {currentPage} of {totalPages} ({totalDocuments} total tests)
                </span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    const newPerPage = parseInt(e.target.value);
                    setPerPage(newPerPage);
                    setCurrentPage(1);
                    fetchTests(1, newPerPage, searchText);
                  }}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={30}>30 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (currentPage > 1) {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      fetchTests(newPage, perPage, searchText);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => {
                          setCurrentPage(pageNum);
                          fetchTests(pageNum, perPage, searchText);
                        }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      fetchTests(newPage, perPage, searchText);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PublishTestErrorModal
        details={publishPointsError?.details}
        test={publishPointsError?.test}
        onClose={() => setPublishPointsError(null)}
        onEditQuestions={() => {
          const test = publishPointsError.test;
          setPublishPointsError(null);
          navigateToTestDetail(test, "view");
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isVisible={showConfirmModal}
        title={confirmAction === "delete" ? "Delete Test" : "Change Test Status"}
        description={
          confirmAction === "delete"
            ? `Are you sure you want to delete "${selectedTest?.title}"? This action cannot be undone and will permanently remove all associated questions and data.`
            : `Are you sure you want to ${selectedTest?.status === "draft" ? "publish" : "move to draft"} "${selectedTest?.title}"?`
        }
        onConfirm={confirmActionHandler}
        onCancel={cancelActionHandler}
        deleting={confirmAction === "delete" ? deleting : actionLoading}
      />

      {/* Create Test Modal */}
      {isCreateModalOpen && (
        <AddTestModal
          closeModal={toggleCreateModal}
          onTestAdded={() => {
            // Reset to page 1 after creating a new test
            setCurrentPage(1);
            fetchTests(1, perPage, searchText);
            fetchClasses();
          }}
          classes={classes}
          school={school}
        />
      )}

      {/* Edit Test Modal */}
      {editingTest && (
        <EditTestModal
          closeModal={() => setEditingTest(null)}
          school={school}
          test={editingTest}
          isAdminMode
          onTestUpdated={() => {
            setEditingTest(null);
            fetchTests(currentPage, perPage, searchText);
          }}
        />
      )}

      {/* Student CBT & Login Access Modal */}
      {isAccessModalOpen && (
        <StudentLoginAccessModal
          isOpen={isAccessModalOpen}
          onClose={() => setIsAccessModalOpen(false)}
          school={school}
        />
      )}
    </>
  );
};

export default CBTExams;
